import { Component, input, output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslatePipe } from '@ngx-translate/core';
import { PaginatorComponent } from '../../navigation/paginator/paginator.component';
import { CellHighlight, TableColumn, TableAction, TableRow, RowActions } from './table.model';


@Component({
  selector: 'app-table',
  standalone: true,
  imports: [CommonModule, MatIconModule, MatButtonModule, MatTooltipModule, TranslatePipe, PaginatorComponent],
  templateUrl: './table.component.html',
  styleUrl: './table.component.css',
})
export class TableComponent<T extends TableRow> {
  data = input<T[]>([]);
  columns = input<TableColumn[]>([]);
  pageSize = input(10);
  page = input(0);
  total = input(0);
  showPagination = input(true);
  fixedColumns = input(true);
  rowActions = input<RowActions>({});
  getRowActions = input<((row: T) => TableAction[]) | null>(null);

  pageChange = output<number>();
  pageSizeChange = output<number>();
  actionClick = output<{ action: TableAction; row: T }>();

  get paginatedData(): T[] {
    if (!this.data().length) return [];
    const start = this.page() * this.pageSize();
    const end = Math.min(start + this.pageSize(), this.total());
    return this.data().slice(start, end);
  }

  onPageChange(page: number): void {
    this.pageChange.emit(page);
  }

  onPageSizeChange(size: number): void {
    this.pageSizeChange.emit(size);
  }

  onActionClick(action: TableAction, row: any): void {
    action.action(row);
    this.actionClick.emit({ action, row });
  }

  formatCell(value: any, type: string): string {
    switch (type) {
      case 'money':
        return new Intl.NumberFormat('es-MX', {
          style: 'currency',
          currency: 'MXN'
        }).format(value);
      case 'date':
        if (!value) return '';
        const date = new Date(value);
        return new Intl.DateTimeFormat('es-MX', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        }).format(date);
      default:
        return String(value ?? '');
    }
  }

  trackById(index: number, row: T): number {
    return row.id;
  }

  trackByColumn(index: number, column: TableColumn): string {
    return column.key;
  }

  trackByAction(index: number, action: TableAction): string {
    return action.label;
  }

  getCellStyle(column: TableColumn, value: any): Record<string, string> | null {
    if (!column.highlights) return null;
    const highlight = column.highlights[value];
    if (!highlight) return null;

    const style: Record<string, string> = {};
    if (highlight.color) style['color'] = highlight.color;
    if (highlight.background) style['background-color'] = highlight.background;
    if (highlight.bold) style['font-weight'] = '600';
    return Object.keys(style).length > 0 ? style : null;
  }

  hasHighlight(column: TableColumn, value: any): boolean {
    return !!column.highlights && !!column.highlights[value];
  }

  isColFixed(column: TableColumn): boolean {
    return this.fixedColumns() && (column.fixed !== false);
  }

  getActionsForRow(row: T): TableAction[] {
    if (this.getRowActions()) {
      return this.getRowActions()!(row);
    }
    return this.rowActions()[row.id] ?? [];
  }

  getActionColorClass(action: TableAction): string {
    switch (action.color) {
      case 'danger':
        return 'action-btn--danger';
      case 'warning':
        return 'action-btn--warning';
      case 'success':
        return 'action-btn--success';
      default:
        return 'action-btn--primary';
    }
  }
}
